import { BaseScoringMetric, LenderUserHistoricalScores, LenderUserRealTimeScores, RealTimeScore, RealTimeScoringMetric, RealTimeScoresByGroupWithScoringGroup, Score } from 'app/models/pulse/scores.model';
import { PulseUnitType, PULSE_UNIT_TYPES } from 'app/models/pulse/pulse.type';
import { CurrencyPipe, DecimalPipe } from '@angular/common';
import { FormatCondensedSecondsPipe } from 'app/pipes/condensed-hour-minutes/format-seconds.pipe';

/**
 * Parses an ISO date string (YYYY-MM-DD) as a local date, avoiding UTC midnight
 * offset issues that occur when using `new Date('YYYY-MM-DD')` directly.
 */
export function parseLocalDate(dateStr: string): Date {
  return new Date(`${dateStr}T00:00:00`);
}

/**
 * Serializes a Date to an ISO date string (YYYY-MM-DD) using local time,
 * avoiding timezone-shift issues from `toISOString()` (which uses UTC).
 */
export function toIsoDate(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Normalizes `scoringMetrics` on a Score from Record or Array to always be an Array.
 * The API may return either shape; this ensures a consistent array throughout the app.
 */
function normalizeScore<T extends BaseScoringMetric>(score: Score<T>): void {
  const raw = score.scoringMetrics as T[] | Record<string, T>;
  if (raw && !Array.isArray(raw)) {
    score.scoringMetrics = Object.values(raw);
  }
}

function normalizeScoresRecord<T extends BaseScoringMetric>(scores: Record<string, Score<T>>): void {
  for (const score of Object.values(scores)) {
    normalizeScore(score);
  }
}

/**
 * Normalizes all `scoringMetrics` in a LenderUserRealTimeScores response to arrays.
 */
export function normalizeRealTimeScores(data: LenderUserRealTimeScores): void {
  for (const group of data.scoresByGroup) {
    normalizeScoresRecord(group.realTimeScores);
  }
}

/**
 * Normalizes all `scoringMetrics` in a LenderUserHistoricalScores[] response to arrays.
 */
export function normalizeHistoricalScores(data: LenderUserHistoricalScores[]): void {
  for (const entry of data) {
    for (const group of entry.scoresByGroup) {
      normalizeScoresRecord(group.historicalScores);
    }
  }
}

/**
 * Finds the most recent historical date across all users for a specific scoring group.
 * This ensures consistent date selection regardless of array order and properly handles
 * users who may not have data for all dates.
 *
 * @param historicalScores - Array of user historical scores
 * @param scoringGroupId - The ID of the scoring group to filter by
 * @returns The most recent date string, or null if no data exists
 */
export function getMostRecentHistoricalDate(
  historicalScores: LenderUserHistoricalScores[],
  scoringGroupId: number
): string | null {
  let maxDate: string | null = null;
  let maxTime = 0;

  for (const user of historicalScores) {
    const scoreGroup = user.scoresByGroup?.find(g => g.groupId === scoringGroupId);
    const scoresRecord = scoreGroup?.historicalScores ?? {};
    for (const dateStr of Object.keys(scoresRecord)) {
      const time = new Date(dateStr).getTime();
      if (time > maxTime) {
        maxTime = time;
        maxDate = dateStr;
      }
    }
  }

  return maxDate;
}

export interface SingleMetricDisplayInfo {
  isSingleMetric: boolean;
  actualValue: number;
  target: number;
  unitType: PulseUnitType | null;
  progressTowardTarget: number;
  formattedValue: string;
}

export function isSingleMetricGroup(scoringMetrics: unknown[]): boolean {
  return scoringMetrics.length === 1;
}

export function getProgressTowardTarget(actualValue: number, target: number): number {
  if (!target || target <= 0) return 0;
  return Math.min(Math.max(actualValue / target, 0), 1);
}

const currencyPipe = new CurrencyPipe('en-US');
const decimalPipe = new DecimalPipe('en-US');
const formatSecondsPipe = new FormatCondensedSecondsPipe();

function toSeconds(value: number, unitType: PulseUnitType): number {
  switch (unitType) {
    case PULSE_UNIT_TYPES.SECONDS:
      return value;
    case PULSE_UNIT_TYPES.MINUTES:
      return value * 60;
    case PULSE_UNIT_TYPES.HOURS:
      return value * 3600;
    default:
      return value;
  }
}

export function formatMetricValue(value: number, unitType: PulseUnitType | null): string {
  switch (unitType) {
    case PULSE_UNIT_TYPES.SECONDS:
    case PULSE_UNIT_TYPES.MINUTES:
    case PULSE_UNIT_TYPES.HOURS:
      return formatSecondsPipe.transform(toSeconds(value, unitType), {
        includeSeconds: true,
        showEmptyHours: false,
        returnEmptyString: false,
      }) ?? '0s';
    case PULSE_UNIT_TYPES.CURRENCY:
      return currencyPipe.transform(value, 'USD', 'symbol', '1.0-0') ?? '';
    case PULSE_UNIT_TYPES.FLOAT:
      return decimalPipe.transform(value, '1.2-2') ?? '';
    case PULSE_UNIT_TYPES.PERCENTAGE:
      return (decimalPipe.transform(value, '1.0-0') ?? '0') + '%';
    default:
      return decimalPipe.transform(value, '1.0-0') ?? '';
  }
}

export function getScoreGroupDisplayInfo(
  scoreByGroup: RealTimeScoresByGroupWithScoringGroup
): SingleMetricDisplayInfo {
  const realTimeScore = Object.values(scoreByGroup.realTimeScores)[0];
  return getSingleMetricDisplayInfo(realTimeScore);
}

export function getSingleMetricDisplayInfo(realTimeScore: RealTimeScore | null | undefined): SingleMetricDisplayInfo {
  const defaultInfo: SingleMetricDisplayInfo = {
    isSingleMetric: false,
    actualValue: 0,
    target: 0,
    unitType: null,
    progressTowardTarget: 0,
    formattedValue: '',
  };

  if (!realTimeScore?.scoringMetrics) return defaultInfo;

  if (!isSingleMetricGroup(realTimeScore.scoringMetrics)) return defaultInfo;
  const metrics = realTimeScore.scoringMetrics as RealTimeScoringMetric[];

  const metric = metrics[0];
  const target = parseFloat(metric.target) || 0;
  const actualValue = metric.actualValue ?? 0;

  return {
    isSingleMetric: true,
    actualValue,
    target,
    unitType: metric.unitType,
    progressTowardTarget: getProgressTowardTarget(actualValue, target),
    formattedValue: formatMetricValue(actualValue, metric.unitType),
  };
}

